import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Wallet, TrendingUp, DollarSign, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import StaffLayout from '../../layouts/StaffLayout';
import { cashDrawerApi } from '../../services/api';
import { formatCurrency, formatDateTime, formatApiError, Spinner } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';

export default function StaffCashDrawerPage() {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [openingCash, setOpeningCash] = useState('');
  const [actualCash, setActualCash] = useState('');
  const [notes, setNotes] = useState('');

  const { data: shift, isLoading } = useQuery({
    queryKey: ['staff-cash-shift'],
    queryFn: () => cashDrawerApi.getCurrent().then(r => r.data),
    refetchInterval: 30000,
  });

  const { data: history = [] } = useQuery({
    queryKey: ['staff-shift-history'],
    queryFn: () => cashDrawerApi.history().then(r => r.data),
  });

  const openMutation = useMutation({
    mutationFn: () => cashDrawerApi.open({ opening_cash: parseFloat(openingCash) || 0, notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff-cash-shift'] });
      toast.success(t('Shift dibuka!', 'Shift opened!'));
      setOpeningCash(''); setNotes('');
    },
    onError: err => toast.error(formatApiError(err)),
  });

  const closeMutation = useMutation({
    mutationFn: () => cashDrawerApi.close({ actual_cash: parseFloat(actualCash) || 0, notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff-cash-shift', 'staff-shift-history'] });
      toast.success(t('Shift ditutup!', 'Shift closed!'));
      setActualCash(''); setNotes('');
    },
    onError: err => toast.error(formatApiError(err)),
  });

  if (isLoading) return (
    <StaffLayout>
      <div className="flex items-center justify-center h-48">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0f6c44] rounded-full animate-spin" />
      </div>
    </StaffLayout>
  );

  return (
    <StaffLayout>
      <div className="max-w-lg mx-auto space-y-5">
        <div>
          <h1 className="text-xl font-bold text-slate-900 font-manrope">{t('Laci Kas Saya', 'My Cash Drawer')}</h1>
          <p className="text-slate-500 text-sm mt-0.5">{t('Kelola buka/tutup shift kas harian', 'Manage your daily cash shift')}</p>
        </div>

        {shift ? (
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Wallet size={20} className="text-[#0f6c44]" />
              </div>
              <div>
                <p className="font-bold text-slate-800 font-manrope">{t('Shift Sedang Berjalan', 'Shift Active')}</p>
                <p className="text-xs text-slate-400">{t('Dibuka', 'Opened')}: {formatDateTime(shift.opened_at)}</p>
              </div>
              <span className="ml-auto bg-emerald-100 text-emerald-700 text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />{t('Aktif', 'Active')}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-5">
              {[
                { label: t('Modal Awal', 'Opening'), value: shift.opening_cash, color: 'bg-blue-50 text-blue-700' },
                { label: t('Kas Masuk', 'Cash In'), value: shift.cash_received || 0, color: 'bg-green-50 text-green-700' },
                { label: t('Ekspektasi', 'Expected'), value: shift.expected_cash || 0, color: 'bg-purple-50 text-purple-700' },
              ].map(item => (
                <div key={item.label} className={`${item.color.split(' ')[0]} rounded-xl p-3 text-center`}>
                  <p className="text-xs mb-1 opacity-70">{item.label}</p>
                  <p className={`text-sm font-bold ${item.color.split(' ')[1]} font-manrope`}>{formatCurrency(item.value)}</p>
                </div>
              ))}
            </div>

            <div className="border-t border-slate-100 pt-4 space-y-3">
              <h4 className="font-semibold text-slate-800">{t('Tutup Shift', 'Close Shift')}</h4>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">{t('Jumlah Kas Aktual', 'Actual Cash Count')}</label>
                <input type="number" value={actualCash} onChange={e => setActualCash(e.target.value)}
                  placeholder={`${t('Ekspektasi', 'Expected')}: ${formatCurrency(shift.expected_cash || 0)}`}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44]" />
              </div>
              {actualCash && (
                <div className={`flex items-center gap-2 p-3 rounded-xl ${parseFloat(actualCash) >= (shift.expected_cash || 0) ? 'bg-green-50' : 'bg-red-50'}`}>
                  {parseFloat(actualCash) >= (shift.expected_cash || 0)
                    ? <CheckCircle size={16} className="text-green-600" />
                    : <AlertTriangle size={16} className="text-red-500" />}
                  <span className={`text-sm font-medium ${parseFloat(actualCash) >= (shift.expected_cash || 0) ? 'text-green-700' : 'text-red-600'}`}>
                    {t('Selisih', 'Difference')}: {formatCurrency(parseFloat(actualCash) - (shift.expected_cash || 0))}
                  </span>
                </div>
              )}
              <input value={notes} onChange={e => setNotes(e.target.value)}
                placeholder={t('Catatan penutupan (opsional)', 'Closing notes (optional)')}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none" />
              <button onClick={() => closeMutation.mutate()} disabled={closeMutation.isPending || !actualCash}
                className="w-full h-12 bg-slate-800 text-white rounded-xl font-semibold hover:bg-slate-700 active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-70">
                {closeMutation.isPending ? <Spinner size="sm" className="border-white/30 border-t-white" /> : t('Tutup Shift', 'Close Shift')}
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 bg-slate-100 rounded-xl flex items-center justify-center">
                <Clock size={20} className="text-slate-400" />
              </div>
              <div>
                <p className="font-bold text-slate-800 font-manrope">{t('Shift Belum Dibuka', 'No Active Shift')}</p>
                <p className="text-xs text-slate-400">{t('Buka shift untuk memulai', 'Open a shift to start')}</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">{t('Modal Awal (Kas dalam laci)', 'Opening Cash')}</label>
                <input type="number" value={openingCash} onChange={e => setOpeningCash(e.target.value)} placeholder="0" autoFocus
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44]" />
              </div>
              <input value={notes} onChange={e => setNotes(e.target.value)}
                placeholder={t('Catatan (opsional)', 'Notes (optional)')}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none" />
              <button onClick={() => openMutation.mutate()} disabled={openMutation.isPending}
                className="w-full h-12 bg-[#0f6c44] text-white rounded-xl font-semibold hover:bg-[#0c5636] active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-70">
                {openMutation.isPending ? <Spinner size="sm" className="border-white/30 border-t-white" /> : <><Wallet size={18} />{t('Buka Shift', 'Open Shift')}</>}
              </button>
            </div>
          </div>
        )}

        {history.filter(s => s.status === 'closed').length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <h3 className="font-semibold text-slate-800 mb-4 font-manrope">{t('Riwayat Shift Saya', 'My Shift History')}</h3>
            <div className="space-y-2">
              {history.filter(s => s.status === 'closed').slice(0, 5).map(s => (
                <div key={s.id} className="flex items-center justify-between p-3.5 bg-slate-50 rounded-xl">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{formatDateTime(s.opened_at)}</p>
                    <p className="text-xs text-slate-400">{t('Modal', 'Opening')}: {formatCurrency(s.opening_cash)}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-semibold ${(s.difference || 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                      {(s.difference || 0) >= 0 ? '+' : ''}{formatCurrency(s.difference || 0)}
                    </p>
                    <p className="text-xs text-slate-400">{t('Selisih', 'Diff')}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </StaffLayout>
  );
}
